import PropTypes from "prop-types";

import InteractiveMultipleChoice from "../InteractiveMultipleChoice";
import MultipleChoice from "../MultipleChoice";
import QuestionText from "../QuestionText";
import sortQuestionOptionsByPosition from "../utils/sortQuestionOptionsByPosition";
import { fieldsetCss, listCss } from "./PowerWordsQuestion.styles";

const propTypes = {
  /**
   * The level-specific content id of the current power words
   */
  contentId: PropTypes.string,
  correctAnswer: PropTypes.string,
  currentLevelId: PropTypes.string,
  isPresentModeActive: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  isStudent: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  powerWordsActivityId: PropTypes.string,
  question: PropTypes.object,
  selectedChoiceContentId: PropTypes.string,
  selectedAnswers: PropTypes.object,
  showAnswerInPresentMode: PropTypes.bool,
  updateSelectedAnswers: PropTypes.func,
};

const PowerWordsQuestion = ({
  contentId,
  correctAnswer,
  isPreviewMode,
  isStudent,
  isSubmitted,
  question,
  selectedChoiceContentId,
  selectedAnswers,
  updateSelectedAnswers,
  isPresentModeActive,
  showAnswerInPresentMode,
}) => {
  const handleSelect = (optionId) => {
    if (isSubmitted) return;
    updateSelectedAnswers(optionId);
  };

  const sortedOptions = sortQuestionOptionsByPosition(question?.options);

  const renderStaticMultipleChoiceQuestion = () => {
    return (
      <>
        <QuestionText
          isPreviewMode={isPreviewMode}
          isStatic
          text={question?.question}
          id={question?.id}
        />
        <ul role="list" css={listCss}>
          {sortedOptions?.map((option) => {
            return (
              <MultipleChoice
                key={option.id}
                choiceIndex={option.position - 1}
                choiceText={option.text}
                isCorrectChoice={
                  isPresentModeActive && !showAnswerInPresentMode
                    ? null
                    : option.is_correct
                }
                isStudent={isStudent}
                isPreviewMode={isPreviewMode}
              />
            );
          })}
        </ul>
      </>
    );
  };

  const renderInteractiveMultipleChoiceQuestion = () => {
    const isSelectedChoice = (optionId) => {
      return (
        optionId &&
        selectedAnswers[contentId]?.powerWordsQuestions?.[question?.id]
          ?.selectedAnswer === optionId
      );
    };

    const evaluateUserSubmittedAnswer = (optionId) =>
      isSubmitted && correctAnswer === optionId;

    const fieldsetName = `group-${question?.id}`;
    return (
      <fieldset css={fieldsetCss}>
        <QuestionText
          isPreviewMode={isPreviewMode}
          text={question?.question}
          id={question?.id}
        />
        <div role="radiogroup" aria-labelledby={question?.id}>
          {sortedOptions?.map((option) => {
            const optionId = option.id;
            return (
              <InteractiveMultipleChoice
                key={`${question?.id}-${optionId}`}
                choiceIndex={option.position - 1}
                choiceText={option.text}
                groupName={fieldsetName}
                handleSelect={handleSelect}
                isSelected={isSelectedChoice(optionId)}
                isSubmitted={isSubmitted}
                optionId={optionId}
                selectedChoiceContentId={selectedChoiceContentId}
                userSelectedCorrectChoice={evaluateUserSubmittedAnswer(
                  optionId,
                )}
                choiceType="PowerWord-answer"
              />
            );
          })}
        </div>
      </fieldset>
    );
  };

  const renderQuestions = () => {
    if (isPreviewMode) {
      return renderStaticMultipleChoiceQuestion();
    }
    return renderInteractiveMultipleChoiceQuestion();
  };

  return <>{renderQuestions()}</>;
};

PowerWordsQuestion.propTypes = propTypes;

export default PowerWordsQuestion;
